import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import {
    ConcretePlan,
    PlanFactory
} from 'src/app/pages/new-billing/models/plan.model';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { RecurrenceStateService } from 'src/app/services/billing/recurrence-state.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { PlanCardClickEvent } from '../plan-card/plan-card.component';

export type CardPlan = {
  plan: ConcretePlan;
  isPopular?: boolean;
};

@Component({
  selector: 'app-choose-plan-generic',
  templateUrl: './choose-plan-generic.component.html',
  styleUrls: ['./choose-plan-generic.component.scss'],
})
export class ChoosePlanGenericComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input()
  get currentPlan(): ConcretePlan { return this._currentPlan; }
  set currentPlan(value: ConcretePlan) {
    this._currentPlan = value;
    this.rebuildPlans();
  }

  @Input()
  payRecurrence: PricePeriodicity = 'year';

  @Input() isV2 = false;

  /** explicit-plan-selection B/C: show card-entry oriented section title on v2 Choose Your Plan. */
  @Input() useCardEntryExperimentTitle = false;

  @Output()
  planCardBtnClick = new EventEmitter<PlanCardClickEvent>();

  @Output()
  payRecurrenceChange = new EventEmitter<PricePeriodicity>();

  private _currentPlan: ConcretePlan;
  private readonly destroy$ = new Subject<void>();
  private scrollTimeout: any = null;
  protected isStandardDefault = false;

  plans: CardPlan[] = [];

  // V2 only state
  userCount: number | null = 1;

  private static readonly mobileBreakpoint = 640;
  private static readonly scrollOffset = 40;
  private static readonly scrollDelay = 100;

  constructor(
    private growthBookService: GrowthBookService,
    private readonly recurrenceStateService: RecurrenceStateService,
  ) { }

  ngOnInit(): void {
    this.recurrenceStateService.payRecurrence$
      .pipe(takeUntil(this.destroy$))
      .subscribe(recurrence => {
        if (this.payRecurrence !== recurrence) {
          this.payRecurrence = recurrence;
          this.payRecurrenceChange.emit(recurrence);
        }
      });

    if (this.isV2) {
      this.growthBookService.getFeature<ExperimentProps>('standard-default-pricing-page')
        .pipe(
          map(value => value?.variationid === 'B'),
          distinctUntilChanged(),
          takeUntil(this.destroy$),
        )
        .subscribe(isB => {
          this.isStandardDefault = isB;
          this.rebuildPlans();
        });
    }
  }

  private rebuildPlans(): void {
    if (!this._currentPlan) return;
    this.plans = PlanFactory.createCardPlans(this._currentPlan, this.growthBookService, this.isV2 && this.isStandardDefault);
  }

  ngOnDestroy(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.scrollToSecondToLast();
  }

  private scrollToSecondToLast() {
    if (!this.isV2) return;
    if (window.innerWidth > ChoosePlanGenericComponent.mobileBreakpoint) return;

    this.scrollTimeout = setTimeout(() => {
      const wrapper = document.querySelector('.plan-cards-wrapper');
      const cards = wrapper?.children;

      if (!cards || cards.length < 2) return;

      const secondToLastCard = cards[cards.length - 2] as HTMLElement;
      const targetScrollLeft = secondToLastCard.offsetLeft - ChoosePlanGenericComponent.scrollOffset;

      wrapper.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }, ChoosePlanGenericComponent.scrollDelay);
  }

  handlePayRecurrenceChange(event: MatRadioChange) {
    // Update the shared state service
    this.recurrenceStateService.setPayRecurrence(event.value);

    // Also emit locally for backward compatibility
    this.payRecurrenceChange.emit(event.value);
    this.payRecurrence = event.value;
  }

  handleRecurrenceButtonClick(recurrence: PricePeriodicity) {
    // Update the shared state service
    this.recurrenceStateService.setPayRecurrence(recurrence);

    // Also emit locally for backward compatibility
    this.payRecurrenceChange.emit(recurrence);
    this.payRecurrence = recurrence;
  }

  onUserCountChange(count: number | null) {
    this.userCount = count;
  }
}
